/**
 * @fileoverview Minimal plug-and-play tabs custom element
 *
 * @example
 * // Basic usage
 * <tabs-element default="overview">
 *   <tabs-list>
 *     <tabs-trigger value="overview">Overview</tabs-trigger>
 *     <tabs-trigger value="details">Details</tabs-trigger>
 *   </tabs-list>
 *   <tabs-content value="overview">
 *     <p>Overview content</p>
 *   </tabs-content>
 *   <tabs-content value="details">
 *     <p>Details content</p>
 *   </tabs-content>
 * </tabs-element>
 *
 * @example
 * // Programmatic activation via window event
 * window.dispatchEvent(new CustomEvent('activate-tabs-my-tabs', { detail: { value: 'details' } }));
 */

// Inject functional styles once — visual styling is left to the implementer
if (!document.getElementById('component-tabs-styles')) {
  const style = document.createElement('style');
  style.id = 'component-tabs-styles';
  style.textContent = `
    tabs-content { display: none; }
    tabs-content.is-active { display: block; }
    tabs-trigger { cursor: pointer; }

    @media (prefers-reduced-motion: no-preference) {
      tabs-content.is-active {
        animation: tabs-fade-in var(--tabs-transition-duration, 0.2s) ease;
      }
    }

    @keyframes tabs-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

class TabsElement extends HTMLElement {
  #activeValue = null;
  #boundActivate = null;

  connectedCallback() {
    this.#boundActivate = (e) => this.activate(e.detail?.value);

    // Window event for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`activate-tabs-${id}`, this.#boundActivate);
    }

    // Activate default tab, fall back to first trigger
    const defaultValue =
      this.getAttribute('default') ??
      this.querySelector(':scope > tabs-list > tabs-trigger')?.getAttribute('value');

    if (defaultValue) {
      this.activate(defaultValue, false);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`activate-tabs-${id}`, this.#boundActivate);
    }
  }

  /**
   * Activates a tab by value.
   * @param {string} value - The value matching a tabs-trigger and tabs-content
   * @param {boolean} dispatch - Whether to dispatch tabs:change event (default true)
   */
  activate(value, dispatch = true) {
    if (!value || value === this.#activeValue) return;
    this.#activeValue = value;

    this.querySelectorAll(':scope > tabs-list > tabs-trigger').forEach((trigger) => {
      const isActive = trigger.getAttribute('value') === value;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.querySelectorAll(':scope > tabs-content').forEach((content) => {
      content.classList.toggle('is-active', content.getAttribute('value') === value);
    });

    if (dispatch) {
      this.dispatchEvent(new CustomEvent('tabs:change', { bubbles: true, detail: { value } }));
    }
  }

  /** @returns {string|null} The currently active tab value */
  get activeValue() {
    return this.#activeValue;
  }
}

class TabsList extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tablist');
  }
}

class TabsTrigger extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', '0');

    this.addEventListener('click', () => {
      this.closest('tabs-element')?.activate(this.getAttribute('value'));
    });

    // Arrow key navigation between triggers
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
        return;
      }

      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;

      e.preventDefault();
      const triggers = [
        ...this.closest('tabs-list')?.querySelectorAll('tabs-trigger') ?? [],
      ];
      const current = triggers.indexOf(this);
      const next =
        e.key === 'ArrowRight'
          ? (current + 1) % triggers.length
          : (current - 1 + triggers.length) % triggers.length;

      triggers[next]?.click();
      triggers[next]?.focus();
    });
  }
}

class TabsContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'tabpanel');
  }
}

customElements.define('tabs-element', TabsElement);
customElements.define('tabs-list', TabsList);
customElements.define('tabs-trigger', TabsTrigger);
customElements.define('tabs-content', TabsContent);
